import type { ResolvedEdgeStyle } from "../types/style.js";
import type { HopMark, LayoutEdge } from "./layout-types.js";
import { el, escapeXml } from "./svg-builder.js";
import { canvasLabel, type LabelWithheld } from "./edge-label-disclosure.js";
import { pairLabelSegment } from "./mutual-pair.js";
import { round2 } from "./svg-number.js";

interface Point {
  x: number;
  y: number;
}

/**
 * The segment index of `points` whose line the hop's centre lies on, or -1.
 * A hop is derived from a crossing *on* one of the edge's segments, so the
 * centre projects onto that segment with ~zero perpendicular distance.
 */
function hopSegment(points: Point[], hop: HopMark): number {
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i];
    const q = points[i + 1];
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) continue;
    const t = ((hop.x - p.x) * dx + (hop.y - p.y) * dy) / len2;
    if (t < -1e-6 || t > 1 + 1e-6) continue;
    const distX = hop.x - (p.x + dx * t);
    const distY = hop.y - (p.y + dy * t);
    if (distX * distX + distY * distY < 0.01) return i;
  }
  return -1;
}

/**
 * Build the edge's stroke path with a **gap** at each hop, so a hop reads as a
 * real jump-over (the arc drawn in the crossing-marks layer bridges the gap)
 * instead of an arc laid on top of a continuous line (a "half-moon"). The gap
 * `[centre ± halfWidth]` along the hosting segment exactly matches the arc's
 * endpoints (same formula), so line and arc meet seamlessly. The crossed line is
 * not a host and stays continuous — it is the through-line the hop jumps over.
 */
function gappedStrokePath(points: Point[], hops: HopMark[]): string {
  const cutsPerSeg = new Map<number, { tEnter: number; tExit: number }[]>();
  for (const hop of hops) {
    const seg = hopSegment(points, hop);
    if (seg < 0) continue;
    const p = points[seg];
    const q = points[seg + 1];
    const len2 = (q.x - p.x) ** 2 + (q.y - p.y) ** 2;
    if (len2 < 1e-9) continue;
    const rad = (hop.angle * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    const paramOf = (x: number, y: number) =>
      ((x - p.x) * (q.x - p.x) + (y - p.y) * (q.y - p.y)) / len2;
    let tEnter = paramOf(hop.x - hop.halfWidth * dx, hop.y - hop.halfWidth * dy);
    let tExit = paramOf(hop.x + hop.halfWidth * dx, hop.y + hop.halfWidth * dy);
    if (tEnter > tExit) [tEnter, tExit] = [tExit, tEnter];
    tEnter = Math.max(0, tEnter);
    tExit = Math.min(1, tExit);
    if (tExit - tEnter <= 0) continue;
    const list = cutsPerSeg.get(seg);
    if (list) list.push({ tEnter, tExit });
    else cutsPerSeg.set(seg, [{ tEnter, tExit }]);
  }

  let d = `M ${round2(points[0].x)} ${round2(points[0].y)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i];
    const q = points[i + 1];
    const at = (t: number) => `${round2(p.x + (q.x - p.x) * t)} ${round2(p.y + (q.y - p.y) * t)}`;
    for (const { tEnter, tExit } of (cutsPerSeg.get(i) ?? []).sort((a, b) => a.tEnter - b.tEnter)) {
      d += ` L ${at(tEnter)} M ${at(tExit)}`;
    }
    d += ` L ${round2(q.x)} ${round2(q.y)}`;
  }
  return d;
}

const STROKE_DASHARRAY: Record<ResolvedEdgeStyle["strokeStyle"], string | undefined> = {
  solid: undefined,
  dashed: "8 4",
  dotted: "2 2",
};

/**
 * Visible thickness of one facet casing per side, matching the node ring's
 * `RING_WIDTH` so an edge's membership reads at the same weight as a card's.
 *
 * The rings leave a 1px gap between bands; the casings cannot, and deliberately
 * do not try. A ring is its own rect over the canvas, so the background shows
 * through between two of them. Casings are stacked strokes on one line, and the
 * only way to separate them would be to paint a canvas-coloured band in the
 * middle — which is a lie over any non-default background. Abutting bands of
 * equal width is the honest rendering of the same idea.
 */
const CASING_WIDTH = 3;

/**
 * One casing per selected facet, widest first, so each is covered by the next
 * narrower one and band `i` is left showing as the annulus
 * `[strokeWidth/2 + i*W, strokeWidth/2 + (i+1)*W]` around the line. Every band
 * is the same width, and the innermost one — against the line — is the first
 * facet in known-facet order, which is the radial position ring `i` takes
 * around a card.
 *
 * Solid regardless of the edge's `stroke-style`, and with no `marker-end`: a
 * dashed casing would read as a second dashed edge, and a marker would stamp an
 * oversized arrowhead behind the real one. The node rings make the same choice
 * — they ignore `border-style` — because the highlight belongs to the reader's
 * selection, not to the element's styling.
 *
 * Painted through `style=` rather than presentation attributes. Diff mode
 * injects a stylesheet that repaints every `line` / `path` under a
 * `[data-diff-state]` group (`diff/diff-style.ts`), and a selector rule beats a
 * presentation attribute — so on a removed edge the casings would come out red
 * and dashed, losing the facet identity they exist to carry. An inline style
 * outranks that rule, which is what keeps "PII is teal" true in diff mode too.
 */
function renderFacetCasings(
  facets: readonly string[],
  colorOf: ReadonlyMap<string, string>,
  strokeWidth: number,
  strokedShape: (attrs: Record<string, string | number | undefined>) => string,
): string[] {
  const casings: string[] = [];
  for (let i = facets.length - 1; i >= 0; i--) {
    const color = colorOf.get(facets[i]);
    if (!color) continue;
    const width = strokeWidth + 2 * (i + 1) * CASING_WIDTH;
    casings.push(
      strokedShape({
        style: `stroke:${color};stroke-width:${width};stroke-dasharray:none`,
        "stroke-linecap": "butt",
        "data-facet-casing": facets[i],
      }),
    );
  }
  return casings;
}

export function renderEdge(
  edge: LayoutEdge,
  style: ResolvedEdgeStyle,
  markerId: string,
  diffState?: string,
  hops?: HopMark[],
  labelAnchorOverride?: Point,
  /**
   * Selected facets this edge belongs to, in known-facet order (#2544). Empty
   * whenever the overlay is off, which is what keeps a file that writes
   * `facets` on an edge byte-identical while nothing is selected (TPL-2174).
   */
  facets: readonly string[] = [],
  /** Colour per facet id; absent when the overlay is off. */
  facetColorOf?: ReadonlyMap<string, string>,
  /**
   * The placement pass could not seat this edge's label clear under
   * `label-display: auto` (#3022), so the canvas leaves it off.
   */
  labelDeferred = false,
  /**
   * This edge's index in `LayoutResult.edges`, set only when a crossing mark
   * belongs to it. The marks carry the same index in `data-edges`, which is how
   * hover dims an edge's hops, chips and bands with it (#3090). Left off
   * everywhere else so a canvas without marks renders byte-identical.
   */
  markIndex?: number,
): string {
  const { fromPoint, toPoint } = edge;
  const points: Point[] = [fromPoint, ...(edge.waypoints ?? []), toPoint];
  const parts: string[] = [];
  // When this edge hosts hop marks (#1859 P2c-C), draw its stroke with a gap at
  // each hop so the arc reads as a jump-over, not a bump on a solid line.
  const gapped = hops !== undefined && hops.length > 0;

  // Group-by against-flow edges are dashed (#1859) so backward inter-group
  // dependencies stand out — but only when the author left `stroke-style` at
  // its default, so an explicit style still wins (mirrors the label-position
  // override precedence, ADR-1185).
  const dashArray =
    edge.groupBackward && style.strokeStyle === "solid"
      ? STROKE_DASHARRAY.dashed
      : STROKE_DASHARRAY[style.strokeStyle];

  const strokeAttrs = {
    stroke: style.color,
    "stroke-width": style.strokeWidth,
    "stroke-dasharray": dashArray,
    "marker-end": `url(#${markerId})`,
    class: edge.cyclic ? "krs-edge--cyclic" : undefined,
  };

  // Two different capabilities, keyed on different things (#2632).
  //
  // Hover and hit belong to every drawn edge: the wide invisible hit-line
  // behind the visible stroke keeps the pointer target practical on default
  // 1.5px edges without changing the diagram's visual weight, and
  // `preview.css` keys the hover highlight and the peer dim (#1186) on
  // `.krs-edge`. A reader can follow any edge they can see, whether or not the
  // author gave it an identity: deploy edges are derived from `realizes`, and
  // ghost edges never get a canonical id, and a base collision clears one.
  //
  // `interactive` drives the `krs-edge--interactive` class, and that class is
  // the *right-click* advertisement only: `preview.css` gives it `cursor:
  // context-menu`, and `handleContextMenu` opens the direction menu only for
  // an edge addressable by an `edge#<id>` selector. Widening it would promise a
  // menu that cannot open (#2543).
  const interactive = edge.canonicalId !== undefined;
  if (points.length === 2) {
    parts.push(
      el("line", {
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
        stroke: "transparent",
        "stroke-width": 14,
        "stroke-linecap": "butt",
        class: "krs-edge__hitline",
      }),
    );
  } else {
    parts.push(
      el("polyline", {
        points: points.map((p) => `${p.x},${p.y}`).join(" "),
        fill: "none",
        stroke: "transparent",
        "stroke-width": 14,
        "stroke-linecap": "butt",
        class: "krs-edge__hitline",
      }),
    );
  }

  /**
   * The edge's own geometry, stroked with `attrs`. One reader for the visible
   * stroke and the facet casings below it, so a casing can never trace a
   * different path than the line it sits under. The geometry itself is resolved
   * once here rather than per call — `gappedStrokePath` walks every hop, and an
   * edge in N facets asks for the same shape N+1 times.
   */
  const geometry: Record<string, string | number | undefined> = gapped
    ? { d: gappedStrokePath(points, hops), fill: "none" }
    : points.length === 2
      ? { x1: fromPoint.x, y1: fromPoint.y, x2: toPoint.x, y2: toPoint.y }
      : { points: points.map((p) => `${p.x},${p.y}`).join(" "), fill: "none" };
  const shapeTag = gapped ? "path" : points.length === 2 ? "line" : "polyline";
  const strokedShape = (attrs: Record<string, string | number | undefined>): string =>
    el(shapeTag, { ...geometry, ...attrs });

  // Facet casings (#2544): the edge counterpart of the node's concentric rings.
  // Drawn widest first and then covered by the narrower ones, so each facet is
  // left showing as its own band and the innermost band — the one against the
  // line — is the same facet on every edge, exactly as `renderFacetRings` makes
  // the innermost ring the same facet on every card. Always solid and never
  // carrying the arrow marker: this is the highlight, not a second edge.
  if (facets.length > 0 && facetColorOf) {
    parts.push(...renderFacetCasings(facets, facetColorOf, style.strokeWidth, strokedShape));
  }

  parts.push(strokedShape(strokeAttrs));

  // The canvas tier of edge-label disclosure (#3022). `drawn` is what the
  // canvas shows of the label; `labelWithheld` says whether that is less than
  // the author wrote, and is set only then, so an edge with nothing withheld
  // is emitted exactly as before (TPL-2174).
  // The default anchor and the segment it lies on: the segment's direction is
  // the arrow a paired half's label carries (#3027), so the anchor is found
  // before the text, the same order the placement pass measures in.
  const defaultAnchor = labelAnchorWithSegment(
    points,
    resolveLabelPosition(edge, style),
    style.labelOffsetX,
    style.labelOffsetY,
    ownLabelSegment(edge),
  );
  const drawn = canvasLabel(edge, style, defaultAnchor.segDir);
  const labelHidden = drawn !== undefined && (drawn.display === "hover" || labelDeferred);
  const labelWithheld: LabelWithheld | undefined =
    drawn === undefined
      ? undefined
      : labelHidden
        ? "deferred"
        : drawn.truncated
          ? "truncated"
          : undefined;
  // Withheld text must stay reachable on this very surface (TPL-3022). The
  // authored label is already on the group as `data-edge-label` for a viewer
  // that brings its own disclosure; the `<title>` is the route for one that
  // brings none, a static SVG included.
  if (labelWithheld !== undefined && edge.label) {
    parts.unshift(el("title", {}, escapeXml(edge.label)));
  }
  if (drawn !== undefined && !labelHidden) {
    // Default anchor (parallel-bundle slide applied, ADR-1185). When the
    // auto label-placement pass (#2048) has nudged this label off a collision,
    // it hands the resolved anchor in via `labelAnchorOverride`, which wins.
    const { x: midX, y: midY } = labelAnchorOverride ?? defaultAnchor.anchor;
    const labelText = el(
      "text",
      {
        x: midX,
        y: midY - 6,
        "text-anchor": "middle",
        fill: style.color,
        "font-size": `${style.fontSize}px`,
        "font-family": "sans-serif",
      },
      drawn.cue
        ? el("tspan", { class: "krs-edge__cue" }, escapeXml(drawn.cue)) +
            escapeXml(drawn.text.slice(drawn.cue.length))
        : escapeXml(drawn.text),
    );

    if (edge.domainEdges && edge.domainEdges.length > 0) {
      // Wrap in a clickable group so PreviewPane can open a detail panel on click.
      // A transparent hit rect widens the click target beyond the text bounding box.
      const hitRect = el("rect", {
        x: midX - 60,
        y: midY - 20,
        width: 120,
        height: 18,
        fill: "transparent",
        "pointer-events": "all",
      });
      parts.push(
        el(
          "g",
          {
            "data-domain-edges": JSON.stringify(edge.domainEdges),
            style: "cursor:pointer",
          },
          hitRect + "\n" + labelText,
        ),
      );
    } else {
      parts.push(labelText);
    }
  }

  return el(
    "g",
    {
      "data-edge-from": edge.from,
      "data-edge-to": edge.to,
      "data-edge-kind": edge.kind,
      "data-edge-canonical-id": edge.canonicalId,
      "data-edge-index": markIndex,
      "data-edge-label": edge.syntheticLabel ? undefined : edge.label || undefined,
      "data-edge-label-withheld": labelWithheld,
      // The property block's payload, read back by the app to open
      // EdgeDetailPanel on a left click (#2543). Emitted only when authored, so
      // a file that writes no block produces byte-identical SVG.
      "data-edge-description": edge.description,
      "data-edge-links":
        edge.links !== undefined && edge.links.length > 0
          ? JSON.stringify(edge.links.map((l) => ({ url: l.url, label: l.label })))
          : undefined,
      // Selected facets this edge belongs to, spelled the way a node card
      // spells them (#2544). Absent while the overlay is off.
      "data-facet-member": facets.length > 0 ? facets.join(" ") : undefined,
      "data-diff-state": diffState,
      class: interactive ? "krs-edge krs-edge--interactive" : "krs-edge",
    },
    parts.join("\n"),
  );
}

/**
 * The fractional label position for an edge: the author's `label-position`
 * (default `0.5`), or — when left at default on a parallel-edge bundle (N ≥ 2
 * sharing `(from, to)`) — a per-index slide so parallel labels separate
 * (ADR-1185). Extracted so the auto label-placement pass (#2048) computes the
 * same default anchor `renderEdge` would draw.
 */
export function resolveLabelPosition(edge: LayoutEdge, style: ResolvedEdgeStyle): number {
  let position = style.labelPosition;
  if (
    position === 0.5 &&
    edge.bundleSize !== undefined &&
    edge.bundleIndex !== undefined &&
    edge.bundleSize >= 2
  ) {
    position = (edge.bundleIndex + 1) / (edge.bundleSize + 1);
  }
  return position;
}

/**
 * Anchor point for the edge label.
 *
 * When `position === 0.5` and both offsets are `0`, the historical
 * "longest segment midpoint" heuristic fires — keeping unstyled
 * diagrams visually stable. Once the author sets `label-position` or
 * `label-offset` on the edge, the renderer switches to fractional
 * traversal (anchor at `position * totalLength` along the polyline) and
 * adds the screen-axis offsets `(offsetX, offsetY)` on top.
 *
 * Offsets are screen-axis (not edge-perpendicular) so a global rule
 * like `edge { label-offset: 0 8px; }` produces a uniform downward
 * shift across the diagram regardless of each edge's slope.
 *
 * `ownSegment` narrows the default for a route whose longest segment is shared
 * with other edges — see {@link ownLabelSegment}.
 *
 * Also returns `segDir`: the direction vector of the polyline segment the
 * anchor lies on (the longest one for the default heuristic, or the one the
 * fractional position falls in). The auto placement pass (#2048) nudges
 * *perpendicular* to this local segment, not the overall `from → to` chord, so
 * the lift stays square to the line the label is drawn on for bent / waypoint
 * routes; a paired half's label takes its arrow from it (#3027).
 */
export function labelAnchorWithSegment(
  points: Point[],
  position: number,
  offsetX: number,
  offsetY: number,
  /**
   * Index of a segment the label must sit on, overriding the longest-segment
   * heuristic. Only consulted on the default path, so an author who sets
   * `label-position` / `label-offset` still wins (ADR-1184).
   */
  ownSegment?: number,
): { anchor: Point; segDir: Point } {
  if (position === 0.5 && offsetX === 0 && offsetY === 0) {
    if (ownSegment !== undefined && ownSegment >= 0 && ownSegment < points.length - 1) {
      const a = points[ownSegment];
      const b = points[ownSegment + 1];
      return {
        anchor: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        segDir: segDirAt(points, ownSegment),
      };
    }
    return defaultLabelAnchor(points);
  }
  return fractionalLabelAnchor(points, position, offsetX, offsetY);
}

/**
 * The segment of an edge's route that belongs to that edge alone, when the rest
 * of the route is shared with its siblings.
 *
 * A trunked edge (#1859 P2c-B) runs: its own stub, then the spine and the
 * target entry, both of which every sibling draws on the same pixels. The
 * longest segment is the spine, so the default heuristic puts N siblings' labels
 * along a line that names none of them: the reader cannot tell which label
 * belongs to which source. The stub is the one part that does, so the label goes
 * there (#2883).
 *
 * A fan-out edge (#2885) is the mirror: it shares the exit and the spine with
 * its siblings and owns only the branch into its own target, the last segment.
 *
 * A lane bundle's route (#2958) may carry channel elbows before or after the
 * spine, so its own segment is the one next to the elbow `trunkJoin` names:
 * the one arriving at it (fan-in) or the one leaving it (fan-out). On a
 * 2-waypoint route those are the first and the last segment.
 *
 * A mutual pair's half (#3027) owns the segments away from the split, see
 * {@link pairLabelSegment}.
 *
 * `undefined` for every other edge, which keeps ADR-1184's default anchor and
 * the byte-stability it was chosen for.
 */
export function ownLabelSegment(edge: LayoutEdge): number | undefined {
  // A paired half (#3027) starts at the split, beside its partner's half.
  if (edge.pairHalf) return pairLabelSegment(edge);
  // The route is `fromPoint, ...waypoints, toPoint`: waypoint `j` is point
  // `j + 1`, so segment `j` arrives at it and segment `j + 1` leaves it.
  if (edge.trunkId !== undefined) return edge.trunkJoin ?? 0;
  // Without `trunkJoin` the elbow is the last waypoint, `waypoints.length - 1`.
  if (edge.outTrunkId !== undefined) {
    return edge.trunkJoin !== undefined ? edge.trunkJoin + 1 : (edge.waypoints?.length ?? 0);
  }
  return undefined;
}

function segDirAt(points: Point[], i: number): Point {
  return { x: points[i + 1].x - points[i].x, y: points[i + 1].y - points[i].y };
}

function defaultLabelAnchor(points: Point[]): { anchor: Point; segDir: Point } {
  if (points.length === 2) {
    return {
      anchor: {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      },
      segDir: segDirAt(points, 0),
    };
  }
  let bestIdx = 0;
  let bestLen = -1;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    const len = dx * dx + dy * dy;
    if (len > bestLen) {
      bestLen = len;
      bestIdx = i;
    }
  }
  return {
    anchor: {
      x: (points[bestIdx].x + points[bestIdx + 1].x) / 2,
      y: (points[bestIdx].y + points[bestIdx + 1].y) / 2,
    },
    segDir: segDirAt(points, bestIdx),
  };
}

function fractionalLabelAnchor(
  points: Point[],
  position: number,
  offsetX: number,
  offsetY: number,
): { anchor: Point; segDir: Point } {
  const segLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    const len = Math.hypot(dx, dy);
    segLengths.push(len);
    total += len;
  }
  if (total === 0) {
    return {
      anchor: { x: points[0].x + offsetX, y: points[0].y + offsetY },
      segDir: segDirAt(points, 0),
    };
  }

  const clamped = Math.min(1, Math.max(0, position));
  const target = clamped * total;
  let acc = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const segLen = segLengths[i];
    const reachable = acc + segLen;
    const isLast = i === segLengths.length - 1;
    if (reachable >= target || isLast) {
      const localT = segLen === 0 ? 0 : (target - acc) / segLen;
      const dx = points[i + 1].x - points[i].x;
      const dy = points[i + 1].y - points[i].y;
      return {
        anchor: {
          x: points[i].x + dx * localT + offsetX,
          y: points[i].y + dy * localT + offsetY,
        },
        segDir: segDirAt(points, i),
      };
    }
    acc += segLen;
  }
  // Unreachable in practice — the loop above covers `isLast`.
  return {
    anchor: { x: points[points.length - 1].x + offsetX, y: points[points.length - 1].y + offsetY },
    segDir: segDirAt(points, points.length - 2),
  };
}

export function renderArrowMarker(id: string, color: string): string {
  return el(
    "marker",
    {
      id,
      viewBox: "0 0 10 10",
      refX: 10,
      refY: 5,
      markerWidth: 8,
      markerHeight: 8,
      orient: "auto-start-reverse",
    },
    el("path", { d: "M 0 0 L 10 5 L 0 10 z", fill: color }),
  );
}
